import { useState } from 'react';
import Modal from '@/components/base/Modal';
import { teacherRoles, classSections, type TeacherRecord } from '@/mocks/teachers';

type OnboardingStep = 1 | 2;

interface TeacherOnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<TeacherRecord>) => void;
  onSaveDraft: (data: Partial<TeacherRecord>) => void;
}

const steps = [
  { step: 1 as OnboardingStep, label: 'Profile', icon: 'ri-user-line' },
  { step: 2 as OnboardingStep, label: 'Assignment', icon: 'ri-briefcase-line' },
];

export default function TeacherOnboardingModal({
  open,
  onClose,
  onSubmit,
  onSaveDraft,
}: TeacherOnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1);
  const [formData, setFormData] = useState<Partial<TeacherRecord>>({});

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = () => {
    onSaveDraft({ ...formData, status: 'inactive' });
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    onSubmit({ ...formData, status: 'active' });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canProceedStep1 = (): boolean => {
    return !!(formData.name && formData.dob && formData.role && formData.contactNumber && formData.email);
  };

  const canProceedStep2 = (): boolean => {
    return !!(formData.className && formData.dateOfJoining && formData.district);
  };

  const stepLabels: Record<OnboardingStep, string> = {
    1: 'Teacher Profile',
    2: 'Class & Assignment Details',
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={stepLabels[currentStep]}
      subtitle={`Step ${currentStep} of 2`}
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div>
            <button
              onClick={handleSaveDraft}
              className="inline-flex items-center gap-2 h-10 px-4 bg-foreground-100 text-foreground-700 text-sm font-label font-medium rounded-lg hover:bg-foreground-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-save-line" />
              Save Draft
            </button>
          </div>
          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(1)}
                className="inline-flex items-center gap-2 h-10 px-4 bg-background-100 text-foreground-600 text-sm font-label font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                Previous
              </button>
            )}
            {currentStep === 1 ? (
              <button
                onClick={() => setCurrentStep(2)}
                disabled={!canProceedStep1()}
                className={`inline-flex items-center gap-2 h-10 px-5 text-sm font-label font-medium rounded-lg transition-colors whitespace-nowrap ${
                  canProceedStep1()
                    ? 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                    : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                }`}
              >
                Next
                <i className="ri-arrow-right-line" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceedStep2()}
                className={`inline-flex items-center gap-2 h-10 px-5 text-sm font-label font-medium rounded-lg transition-colors whitespace-nowrap ${
                  canProceedStep2()
                    ? 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                    : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-check-line" />
                Onboard Teacher
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* ── Stepper ─────────────────────────────────────────── */}
      <div className="flex items-center justify-center gap-0 mb-8">
        {steps.map((s, idx) => {
          const isActive = s.step === currentStep;
          const isCompleted = s.step < currentStep;
          return (
            <div key={s.step} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-label font-bold transition-all duration-200 ${
                    isCompleted
                      ? 'bg-primary-500 text-background-50'
                      : isActive
                        ? 'bg-primary-500 text-background-50 ring-4 ring-primary-100'
                        : 'bg-background-100 text-foreground-400'
                  }`}
                >
                  {isCompleted ? <i className="ri-check-line" /> : s.step}
                </div>
                <span
                  className={`mt-2 text-xs font-label font-medium whitespace-nowrap ${
                    isActive ? 'text-primary-600' : isCompleted ? 'text-foreground-600' : 'text-foreground-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-24 sm:w-32 h-0.5 mx-2 mt-[-18px] rounded-full transition-colors ${
                  s.step < currentStep ? 'bg-primary-500' : 'bg-background-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Step 1: Profile ─────────────────────────────────── */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Full Name <span className="text-semantic-error">*</span>
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Enter teacher's full name"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Date of Birth <span className="text-semantic-error">*</span>
              </label>
              <input
                type="date"
                value={formData.dob || ''}
                onChange={(e) => updateField('dob', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Role <span className="text-semantic-error">*</span>
              </label>
              <select
                value={formData.role || ''}
                onChange={(e) => updateField('role', e.target.value)}
                className="xl-input cursor-pointer"
              >
                <option value="">Select role</option>
                {teacherRoles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Contact Number <span className="text-semantic-error">*</span>
              </label>
              <input
                type="text"
                value={formData.contactNumber || ''}
                onChange={(e) => updateField('contactNumber', e.target.value)}
                placeholder="+91 XXXXX XXXXX"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Email Address <span className="text-semantic-error">*</span>
              </label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="teacher@xlplatform.edu"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="text-sm font-medium text-foreground-700 font-label">Address</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="Full residential address"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">District</label>
              <input
                type="text"
                value={formData.district || ''}
                onChange={(e) => updateField('district', e.target.value)}
                placeholder="District name"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Town / City</label>
              <input
                type="text"
                value={formData.town || ''}
                onChange={(e) => updateField('town', e.target.value)}
                placeholder="Town or city name"
                className="xl-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Assignment ──────────────────────────────── */}
      {currentStep === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Class / Section <span className="text-semantic-error">*</span>
              </label>
              <select
                value={formData.className || ''}
                onChange={(e) => updateField('className', e.target.value)}
                className="xl-input cursor-pointer"
              >
                <option value="">Select class section</option>
                {classSections.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Date of Joining <span className="text-semantic-error">*</span>
              </label>
              <input
                type="date"
                value={formData.dateOfJoining || ''}
                onChange={(e) => updateField('dateOfJoining', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="text-sm font-medium text-foreground-700 font-label">Profile Summary</label>
              <p className="text-sm text-foreground-500 font-label">
                {formData.name && formData.role ? (
                  <span className="text-foreground-700 font-medium">{formData.name}</span>
                ) : '—'}
                {formData.role && (
                  <span className="text-foreground-500"> — {formData.role}</span>
                )}
              </p>
              <p className="text-xs text-foreground-400 font-label">
                {formData.className && formData.dateOfJoining ? (
                  <>
                    Assigned to <span className="text-foreground-500 font-medium">{formData.className}</span> · Joining{' '}
                    <span className="text-foreground-500 font-medium">{formData.dateOfJoining}</span>
                  </>
                ) : (
                  'Complete the fields above to see the assignment summary.'
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}